import { useMutation, useQueryClient } from '@tanstack/react-query';

import { workspaceKeys } from '@/features/workspaces';
import type { TaskView, WorkspaceAggregate } from '@/lib/api/generated/model';
import { taskKeys } from '../queryKeys';
/**
 * The cache half of every write that changes some fields of one task.
 *
 * Extracted because `useTaskText` and `useTaskStepAssignee` had grown the same
 * cancel/patch/rollback/invalidate sequence almost line for line, and a fix to the
 * rollback edge cases in one would not have reached the other (raised in review on
 * MR !69). What differs between callers is only *which* fields move, which is what
 * the two callbacks below express.
 *
 * Patch optimistically, take the server's answer on success, and invalidate only
 * when the write fails. The board polls this same aggregate every two seconds, so
 * invalidating on the happy path would re-GET every task, workflow and label in
 * the workspace to learn something the optimistic patch already made true. A
 * failed write is the one case where what the cache holds is genuinely unknown.
 */
function patchTask(
  aggregate: WorkspaceAggregate | null | undefined,
  taskId: string,
  fields: Partial<TaskView>,
): WorkspaceAggregate | null | undefined {
  if (!aggregate) return aggregate;
  return {
    ...aggregate,
    tasks: aggregate.tasks.map((task) =>
      task.id === taskId ? { ...task, ...fields } : task,
    ),
  };
}

// Exported only so the hooks built on this have a return type the emitted
// declarations can name (tsconfig.app.json is a composite project).
export interface MutationContext {
  previousAggregate: WorkspaceAggregate | null | undefined;
}

export interface OptimisticTaskPatchOptions<TInput> {
  /** Performs the write and returns the task as the server now holds it. */
  send: (input: TInput) => Promise<TaskView>;
  /** What to show immediately, before the server has answered. */
  optimistic: (input: TInput) => Partial<TaskView>;
  /**
   * Which fields to take from the server's answer once it lands.
   *
   * Separate from `optimistic` because the two are genuinely different questions:
   * a title comes back trimmed, a whitespace-only description comes back `null`,
   * and a cleared gate assignee comes back as whoever the *workflow* names — none
   * of which the optimistic guess could have known.
   */
  settled: (task: TaskView) => Partial<TaskView>;
}

export function useOptimisticTaskPatch<
  TInput extends { workspaceId: string; taskId: string },
>({ send, optimistic, settled }: OptimisticTaskPatchOptions<TInput>) {
  const queryClient = useQueryClient();

  return useMutation<TaskView, Error, TInput, MutationContext>({
    mutationFn: send,

    onMutate: async (input) => {
      await queryClient.cancelQueries({
        queryKey: workspaceKeys.detail(input.workspaceId),
      });
      const previousAggregate = queryClient.getQueryData<WorkspaceAggregate>(
        workspaceKeys.detail(input.workspaceId),
      );
      queryClient.setQueryData(
        workspaceKeys.detail(input.workspaceId),
        (prev: WorkspaceAggregate | null | undefined) =>
          patchTask(prev, input.taskId, optimistic(input)),
      );
      return { previousAggregate };
    },

    onSuccess: (task, input) => {
      // A rename or description edit of a subtask is logged on its run
      // (spec 148).
      void queryClient.invalidateQueries(
        taskKeys.activityInWorkspace(input.workspaceId),
      );
      queryClient.setQueryData(
        workspaceKeys.detail(input.workspaceId),
        (prev: WorkspaceAggregate | null | undefined) =>
          patchTask(prev, input.taskId, settled(task)),
      );
    },

    onError: (_error, input, context) => {
      if (context) {
        queryClient.setQueryData(
          workspaceKeys.detail(input.workspaceId),
          context.previousAggregate,
        );
      }
      void queryClient.invalidateQueries({
        queryKey: workspaceKeys.detail(input.workspaceId),
      });
    },
  });
}
